import { FilterApplicableFor, FilterType, SavedFilter } from "./types";
import { TableHeaderType } from "../tables/types";
import { useQuery } from "@tanstack/react-query";
import { getSavedFilters } from "@/helpers/apis";
import { useEffect, useMemo, useState } from "react";
import { ComponentLoader } from "../loader";
import SavedFilterComponent from "./SavedFilter";
import classes from "./savedFilters.module.scss";
import {
  Button,
  ButtonProps,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  Typography,
} from "@altimateai/lego";
import { SavedFilterUrlParam } from "../tables/constants";
import { IconX } from "@tabler/icons-react";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";

interface Props {
  applicableFor: FilterApplicableFor;
  buttonProps?: ButtonProps;
  onEdit?: (filter: SavedFilter) => void;
  headers?: TableHeaderType[];
  enableNewUi?: boolean;
}
const SavedFilters = ({ applicableFor, buttonProps, onEdit, headers, enableNewUi }: Props) => {
  const [open, setOpen] = useState(false);
  const { clearSearchParams, searchParams } = useFiltersBySearchParams();
  const [selectedFilter, setSelectedFilter] = useState("");
  const { data, isLoading, refetch } = useQuery({
    queryKey: [`SAVED_FILTERS_${applicableFor}`],
    queryFn: () => getSavedFilters({ applicableFor, type: FilterType.FILTER }),
  });

  const savedFilterId = searchParams.get(SavedFilterUrlParam);

  useEffect(() => {
    setSelectedFilter(savedFilterId || "");
  }, [savedFilterId]);

  const onShowFilter = (event: CustomEvent<string>) => {
    if (event.detail) {
      refetch();
      setSelectedFilter(event.detail);
      setOpen(true);
    }
  };
  useEffect(() => {
    document.addEventListener("showFilter", onShowFilter as EventListener);
    return () => {
      document.removeEventListener("showFilter", onShowFilter as EventListener);
    };
  }, []);

  const selectedFilterData = useMemo(() => {
    if (!selectedFilter || !data) {
      return null;
    }

    return data?.presets.find(p => p.id.toString() === selectedFilter);
  }, [selectedFilter, data]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="secondary" {...buttonProps} className="!al-gap-0">
          Saved Filters
          {enableNewUi && selectedFilterData ? (
            <>
              <Typography weight="regular" size="sm" className="al-text-brand">
                : {selectedFilterData.name}
              </Typography>
              <Button
                variant="ghost"
                className="!al-py-0 !al-px-1"
                onClick={e => {
                  e.stopPropagation();
                  clearSearchParams();
                }}
              >
                <IconX />
              </Button>
            </>
          ) : (
            ""
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="!al-max-w-[50vw] al-min-w-[600px]">
        <SheetHeader>
          <SheetTitle>Saved Filters</SheetTitle>
        </SheetHeader>
        <div className="al-flex-1">
          <div className={classes.saved_filters}>
            <Stack className="al-my-4">
              {isLoading ? (
                <ComponentLoader label="Loading saved filters..." />
              ) : data?.presets.length ? (
                <ul className="al-w-full">
                  {data.presets.map(filter => (
                    <SavedFilterComponent
                      key={filter.id}
                      filter={filter}
                      selectedFilter={selectedFilter}
                      refetch={refetch}
                      onEdit={onEdit}
                      headers={headers}
                      onApplied={() => setOpen(false)}
                    />
                  ))}
                </ul>
              ) : (
                <div>No saved filters found</div>
              )}
            </Stack>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SavedFilters;
